import { paymentsService } from '@/api/payments.service';
import type {
  CreatePaymentDto,
  FindAllPaymentsQueryDto,
  Payment,
  UpdatePaymentDto,
} from '@/entities/payments/payment.entity';
import {
  addItemToQueriesData,
  removeItemFromQueriesData,
  updateItemOnQueriesData,
} from '@/utils/update-query-data';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { type MaybeRefOrGetter, toRef, toValue } from 'vue';
import type { MutationDefaultsContext } from './query-client';
import { INVOICES_QUERY_KEY } from './use-invoices';
import { BOOKINGS_QUERY_KEY } from './use-bookings';
import { LEDGER_ENTRIES_QUERY_KEY, PAYMENT_ALLOCATIONS_QUERY_KEY } from './use-ledger';

export const PAYMENTS_QUERY_KEY = 'payments';

export const usePayment = (id: MaybeRefOrGetter<string>) => {
  return useQuery({
    queryKey: [PAYMENTS_QUERY_KEY, toRef(id)],
    queryFn: () => paymentsService.getOne(toValue(id)),
  });
};

export const usePayments = () => {
  return useQuery({
    queryKey: [PAYMENTS_QUERY_KEY],
    queryFn: () => paymentsService.getAll(),
  });
};

export const usePaymentsWithQuery = (
  query: MaybeRefOrGetter<FindAllPaymentsQueryDto>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useInfiniteQuery({
    queryKey: [PAYMENTS_QUERY_KEY, toRef(query)],
    queryFn: ({ pageParam = 1 }) =>
      paymentsService.getAllWithQuery({
        ...toValue(query),
        page: pageParam,
      }),
    enabled: toValue(enabled),
    getNextPageParam: (lastPage) => lastPage.meta.nextPage,
    initialPageParam: 1,
  });
};

export const useUpdatePayment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [PAYMENTS_QUERY_KEY],
    mutationFn: ({ id, payment }: { id: string; payment: UpdatePaymentDto }) =>
      paymentsService.update(id, payment),
    onMutate: (): MutationDefaultsContext => ({
      successMessage: 'Payment updated',
    }),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(result, variables, onMutateResult, context);
      updateItemOnQueriesData<Payment>([PAYMENTS_QUERY_KEY], result, queryClient);
      void queryClient.invalidateQueries({ queryKey: [INVOICES_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [BOOKINGS_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [LEDGER_ENTRIES_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [PAYMENT_ALLOCATIONS_QUERY_KEY] });
    },
  });
};

export const useCreatePayment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [PAYMENTS_QUERY_KEY],
    mutationFn: (payment: CreatePaymentDto) => paymentsService.create(payment),
    onMutate: (): MutationDefaultsContext => ({
      successMessage: 'Payment saved',
    }),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(result, variables, onMutateResult, context);
      addItemToQueriesData<Payment>([PAYMENTS_QUERY_KEY], result, queryClient);
      void queryClient.invalidateQueries({ queryKey: [INVOICES_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [BOOKINGS_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [LEDGER_ENTRIES_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [PAYMENT_ALLOCATIONS_QUERY_KEY] });
    },
  });
};

export const useRefundUnallocatedPaymentAmount = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [PAYMENTS_QUERY_KEY, 'refund-unallocated'],
    mutationFn: ({ paymentId, amount }: { paymentId: string; amount: number }) =>
      paymentsService.refundUnallocatedPaymentAmount(paymentId, amount),
    onMutate: (): MutationDefaultsContext => ({
      successMessage: 'Refund initiated',
    }),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(result, variables, onMutateResult, context);
      addItemToQueriesData<Payment>(
        [PAYMENTS_QUERY_KEY],
        result.payment as unknown as Payment,
        queryClient,
      );
      void queryClient.invalidateQueries({ queryKey: [INVOICES_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [PAYMENTS_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [BOOKINGS_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [LEDGER_ENTRIES_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [PAYMENT_ALLOCATIONS_QUERY_KEY] });
    },
  });
};

export const useDeletePayment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [PAYMENTS_QUERY_KEY],
    mutationFn: (id: string) => paymentsService.remove(id),
    onMutate: (id) => {
      return { id };
    },
    onSuccess: (result, variables) => {
      removeItemFromQueriesData([PAYMENTS_QUERY_KEY], variables, queryClient);
    },
  });
};
